import {Helmet} from '@dr.pogodin/react-helmet';
import {ColorSchemeType, DataStatus, Segment, Avatar} from 'platform/components';
import {Box, HStack, Spinner, Show, Text, VStack, Clickable, Hide} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Link} from 'react-router-dom';

import {always} from 'ramda';

import {BusinessCaseType} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {businessCaseRoutes, interestRoutes} from '@dms/routes';
import {Main} from '@dms/shared';

import {composePath, DOT_CHARACTER, isLastIndexInArray, pushGtmEvent} from 'shared/web';

import {KanbanCard} from './components/KanbanCard';
import {Settings} from './components/Settings';
import {avatarColors, cardColors} from './constants/kanbanColors';
import {KANBAN_COLUMNS} from './constants/kanbanColumns';
import {KANBAN_SEGMENTS} from './constants/kanbanSegments';
import {useKanbanControl} from './hooks/useKanbanControl';
import {useKanbanSettings} from './hooks/useKanbanSettings';
import {getCardsForColumnValue} from './utils/getCardsForColumnValue';
import {getNonHiddenSelectedUserIds} from './utils/getNonHiddenSelectedUserIds';
import {isColumnVisible} from './utils/isColumnVisible';

export function OpportunitiesKanban() {
  const {
    businessCaseType,
    businessCases,
    interests,
    isLoading,
    isFetching,
    isError,
    onFilterChange,
  } = useKanbanControl();

  const handleFilterChange = (nonHiddenSelectedUserIds: string[]) => {
    onFilterChange(businessCaseType, nonHiddenSelectedUserIds);
  };

  const {
    currentUser,
    users,
    settings,
    isLoading: isSettingsLoading,
    isError: isSettingsErrored,
    onKanbanSettingsChange,
  } = useKanbanSettings(handleFilterChange);

  return (
    <>
      <Helmet title={i18n.t('page.opportunitiesKanban.title')} />
      <Main isFullHeight padding={0}>
        <Box
          position="sticky"
          backgroundColor="palettes.neutral.10.100"
          top={0}
          left={0}
          right={0}
          paddingHorizontal={[2, 4]}
        >
          <DataStatus
            data={settings}
            isLoading={isSettingsLoading}
            isError={isSettingsErrored}
            spinnerSize="small"
          >
            {({data}) => (
              <HStack width="100%" minHeight={[12, 16]} align="center" justify="space-between">
                <Segment<BusinessCaseType>
                  value={businessCaseType}
                  options={KANBAN_SEGMENTS}
                  onChange={(value) => {
                    onFilterChange(value, getNonHiddenSelectedUserIds(data.selectedUsers));
                  }}
                />
                <HStack spacing={2} align="center">
                  <Show when={isFetching}>
                    <Spinner />
                  </Show>
                  <HStack spacing={2} align="center">
                    <Hide when={data.selectedUsers.length < 2} onMobile>
                      {data.selectedUsers.map((user, userIndex) => (
                        <Clickable
                          key={`kanban-settings-${user.id}`}
                          isDisabled={
                            !user.isHidden &&
                            data.selectedUsers.filter((user) => !user.isHidden).length === 1
                          }
                          onClick={() => {
                            onKanbanSettingsChange({
                              visibleColumns: data.visibleColumns,
                              selectedUsers: data.selectedUsers.map((item) =>
                                item.id === user.id ? {...item, isHidden: !item.isHidden} : item
                              ),
                            });
                          }}
                        >
                          <Box opacity={user.isHidden ? 0.2 : 1}>
                            <Avatar
                              name={user.fullName}
                              size="small"
                              color={avatarColors[userIndex]}
                              textColor="white"
                            />
                          </Box>
                        </Clickable>
                      ))}
                    </Hide>
                    <Settings
                      currentUserId={currentUser?.id ?? ''}
                      users={users}
                      settings={data}
                      onSave={onKanbanSettingsChange}
                    />
                  </HStack>
                </HStack>
              </HStack>
            )}
          </DataStatus>
        </Box>
        <DataStatus
          isLoading={isLoading || isSettingsLoading}
          isError={isError}
          isEmpty={
            !isLoading &&
            !isSettingsLoading &&
            !businessCases?.rows?.length &&
            !interests?.rows?.length
          }
          data={settings}
          emptyMessage={i18n.t('page.vehicle.notifications.noBusinessCases')}
          minHeight={200}
        >
          {({data}) => (
            <Box width="100%" height="100%" overflow="auto" paddingBottom={[2, 4]}>
              <HStack spacing={2} height="100%" width="100%">
                {KANBAN_COLUMNS.filter((kanbanColumn) =>
                  isColumnVisible(data, businessCaseType, kanbanColumn.key)
                ).map((column, columnIndex) => {
                  const cards = getCardsForColumnValue(column.key, businessCases, interests);
                  return (
                    <Box
                      key={`business-case-kanban-${column.key}`}
                      width="100%"
                      height="100%"
                      paddingLeft={columnIndex === 0 ? [2, 4] : 0}
                      paddingRight={isLastIndexInArray(KANBAN_COLUMNS, columnIndex) ? [2, 4] : 0}
                    >
                      <Box
                        key={`business-case-kanban-${column.key}`}
                        borderRadius="small"
                        minWidth={[65, 55]}
                        width="100%"
                        height="100%"
                        backgroundColor="palettes.neutral.30.100"
                        padding={2}
                      >
                        <VStack width="100%" spacing={3}>
                          <HStack spacing={1} align="center">
                            <Text size="small" alternative color="primary">
                              {i18n.t(column.label)}
                            </Text>
                            <Text size="small" color="tertiary">
                              {DOT_CHARACTER}
                            </Text>
                            <Text size="small" color="tertiary">
                              {cards.length || 0}
                            </Text>
                          </HStack>
                          {cards.map((card, cardIndex) => (
                            <Link
                              key={`${businessCaseType}-${column.key}-${card.id}-${card.origin}-${cardIndex}`}
                              to={composePath(
                                match([card.origin, businessCaseType])
                                  .with(
                                    ['business-case', 'BUYING'],
                                    () => businessCaseRoutes.buying
                                  )
                                  .with(
                                    ['business-case', 'SELLING'],
                                    () => businessCaseRoutes.selling
                                  )
                                  .otherwise(() => interestRoutes.detailOverview),
                                {
                                  params: {id: card.id},
                                  queryParams: {
                                    source: 'opportunities-kanban',
                                  },
                                }
                              )}
                              onClick={() => {
                                pushGtmEvent({
                                  event: 'button_click',
                                  event_id: 'opportunities_kanban_card_click',
                                  value: {
                                    card_id: card.id,
                                    card_origin: card.origin,
                                    card_business_case_type: businessCaseType,
                                  },
                                });
                              }}
                            >
                              <KanbanCard
                                card={card}
                                backgroundColor={
                                  data.selectedUsers.length > 1
                                    ? cardColors[
                                        data.selectedUsers.findIndex(
                                          (user) => user.id === card.ownerId
                                        )
                                      ]
                                    : null
                                }
                                colorStripe={match<number, ColorSchemeType>(columnIndex)
                                  .with(0, always('neutral'))
                                  .with(1, always('yellow'))
                                  .with(2, always('orange'))
                                  .with(3, always('teal'))
                                  .with(4, always('blue'))
                                  .with(5, always('purple'))
                                  .with(6, always('magenta'))
                                  .with(7, always('green'))
                                  .otherwise(always('black'))}
                              />
                            </Link>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  );
                })}
              </HStack>
            </Box>
          )}
        </DataStatus>
      </Main>
    </>
  );
}
